// assembly (MES / Production) board: the stage load (the build journey strip, enlarged to one tick per
// boat per stage), the smart schedule, a finite-capacity plan, the production report + rolling daily log,
// and a per-vessel traveler. all logic in @technohull/shared/mes.
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  INPROD_THRESHOLD_HOURS,
  JOURNEY_STAGES,
  MES_STAGES,
  MES_WORK,
  addWorkingDays,
  fmtHrs,
  journeyOf,
  mesHolds,
  mesNextOp,
  mesOverdue,
  mesPriority,
  mesRemaining,
  mesSchedule,
  mesStageOf,
  parseOvDate,
  teamLabel,
  teamOf,
  type JourneyState,
  type JourneyStep,
  type MesNextOp,
  type MesPriority,
  type OverviewVessel,
  type ReportGroup,
  type ReportSummary,
  type Team,
  type VesselSummary,
} from '@technohull/shared';
import {
  useGrp,
  useOverview,
  useProductionVessels,
  useReport,
  useReportLog,
  type ReportLogEntry,
  type ReportLogMetrics,
} from '../features/hooks.js';
import { idleReady, idleSignalFrom, placeFleet, startOfTodayUtc } from '../features/fleetPlacement.js';
import { exportMesBoard } from '../features/exportMesBoard.js';
import { useDateFormatter, useFormatDate } from '../lib/format.js';
import { MQ, useBackToClose, useMediaQuery } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';
import { useTabState } from '../lib/tabState.js';
import { useActiveSeason } from '../season/SeasonContext.js';
import { useAuth } from '../auth/AuthContext.js';
import { ActionBar, ErrorState, Fact, FactEnd, FactSentence, InlineError, InlineLoading, LoadingState, LogoSpin, PageHeader, Segmented } from '../components/ui.js';
import { JourneyStrip, journeyStateWord } from '../components/JourneyStrip.js';
import {
  IconAlertTriangle, IconCheck, IconChevronRight, IconClose, IconDiamond, IconDownload,
  IconHourglass, IconLightbulb, IconPause, IconPlay, IconRefresh, IconSearch, IconTool, IconTriangleUp,
} from '../components/icons.js';

type View = 'board' | 'schedule' | 'planning';
const VIEWS: Array<[View, string]> = [['board', 'Board'], ['schedule', 'Smart schedule'], ['planning', 'Planning']];
const TEAMS: Team[] = ['GGO', 'ALPHAS', 'XPD'];
const DAY = 86400000;
const LAST_STEP = JOURNEY_STAGES.length - 1;
// the stage names the phone's seven narrow columns use
const SHORT: Record<string, string> = { grp: 'GRP', assembly: 'Asm', engine: 'Eng', startup: 'Start', seatrial: 'Trial', pdi: 'PDI', delivery: 'Dlv' };
// chips past this many in one stage fold behind "+N more"
const CHIP_CAP = 12;

const fmtNum = (v: number | null | undefined): string => {
  if (v == null || isNaN(v)) return '-';
  const a = Math.abs(v);
  if (a >= 100) return Math.round(v).toLocaleString('en-GB');
  if (a >= 1) return String(Math.round(v * 10) / 10);
  return String(Math.round(v * 10000) / 10000);
};

// keyboard-operable clickable row/card: role=button + Enter/Space, same as Agenda's entries
const clickable = (fn: () => void) => ({
  role: 'button' as const, tabIndex: 0, onClick: fn,
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); } },
});

// ── Per-vessel traveler (a dialog; a bottom sheet on phones) ─────────────────────────────────────
function MesTraveler({ v, today, onClose }: { v: OverviewVessel; today: Date; onClose: () => void }) {
  const fmt = useFormatDate();
  const chain: Array<[string, string]> = [
    ['GRP start', v.grpStart], ['Assembly start', v.assemblyStart],
    ['Engine install', v.engineInstallDate], ['Engine start-up', v.engineStartupDate],
    ['Assembly finish', v.assemblyFinish], ['Sea trial', v.seaTrialDate],
    ['PDI', v.pdiDate], ['Delivery', v.deliveryDate],
  ];
  let prevMax: Date | null = null;
  const steps = chain.map(([label, d], i) => {
    const dt = parseOvDate(d);
    const done = !!dt && dt <= today;
    let gapNode: JSX.Element | null = null;
    let reversed = false;
    if (dt && prevMax) {
      const g = Math.round((+dt - +prevMax) / DAY);
      reversed = g < -60;
      gapNode = (
        <span className={`mes-tv-gap${reversed ? ' rev' : ''}`} title={reversed ? 'Dated well before an earlier milestone - likely a data error' : 'days vs the previous milestone'}>
          {reversed ? <><IconAlertTriangle size={11} />{' '}</> : null}{g >= 0 ? '+' : ''}{g}d
        </span>
      );
    }
    if (dt && (!prevMax || dt > prevMax)) prevMax = dt;
    return (
      <li key={i} className={`mes-tv-step ${done ? 'done' : dt ? 'sched' : 'pending'}${reversed ? ' reversed' : ''}`}>
        <span className="mes-tv-dot" aria-hidden="true" />
        <span className="mes-tv-label">{label}</span>
        <span className="mes-tv-date">{fmt(dt) || '-'}</span>
        {gapNode ?? <span className="mes-tv-gap" />}
      </li>
    );
  });
  const holds = mesHolds(v);
  const journey = journeyOf(v, today);
  useBackToClose(true, onClose); // mounted only while open; on touch the back gesture closes it
  // closes on Escape like every other dialog (it can be opened from the keyboard). The latest onClose
  // sits in a ref so the listener is attached once, not again on every render of the Board.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const boxRef = useRef<HTMLDivElement>(null);
  // whatever opened the dialog (a row's Open, a chip), read during the first render: by the time an
  // effect runs, the close button's autoFocus has already moved focus into the dialog
  const [opener] = useState(() => (document.activeElement instanceof HTMLElement ? document.activeElement : null));
  useEffect(() => {
    // focus goes back to the opener when the dialog closes, and Tab stays inside it while it is open,
    // as with the app's other dialogs
    const k = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') { closeRef.current(); return; }
      if (e.key !== 'Tab' || !boxRef.current) return;
      const f = [...boxRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => !el.hasAttribute('disabled'));
      if (!f.length) return;
      const first = f[0]!, last = f[f.length - 1]!;
      if (e.shiftKey && (document.activeElement === first || !boxRef.current.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !boxRef.current.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', k);
    return () => {
      window.removeEventListener('keydown', k);
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    };
  }, [opener]);
  return (
    <div className="mes-traveler-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={boxRef} className="mes-traveler" role="dialog" aria-modal="true" aria-label={`${v.sap} traveler`}>
        <div className="mes-tv-hd">
          <div className="mes-tv-id">
            <h2 className="mes-tv-sap">{v.sap}</h2>
            <div className="mes-tv-name">{v.name || '-'} · {v.model || '-'}</div>
          </div>
          <button className="modal-x" aria-label="Close" autoFocus onClick={onClose}><IconClose size={18} /></button>
        </div>
        <div className="mes-tv-journey"><JourneyStrip steps={journey} size="lg" labels /></div>
        {mesOverdue(v, today) && <div className="mes-tv-alert ov"><IconAlertTriangle size={15} /> Planned delivery date has passed - not yet delivered</div>}
        {holds.length > 0 && <div className="mes-tv-alert hold"><IconPause size={15} /> On hold: {holds.join(', ')}</div>}
        <ol className="mes-tv-body">{steps}</ol>
        <div className="mes-tv-ft">Assembly completion <b>{v.assemblyPct ? v.assemblyPct : '-'}</b></div>
      </div>
    </div>
  );
}

// ── report tables (team + by-model) ─────────────────────────────────────────────
const REPORT_COLS: Array<[string, keyof ReportGroup]> = [
  ['HoursCompleted', 'hoursCompleted'], ['HRS Needed', 'hoursNeeded'], ['Days Avail.', 'daysAvail'],
  ['Theoretical HRS Available', 'theoretical'], ['Actual HRS 60%', 'actual60'], ['HRS Surplus', 'surplus'],
  ['Daily Rate of Change', 'dailyRate'], ['Daily Ideal Rate of Change', 'dailyIdeal'],
  ['Daily Hrs Completed', 'dailyDone'], ['Ideal Daily Hrs Completed', 'idealDone'],
];

function ReportTables({ report }: { report: ReportSummary }) {
  const fmt = useFormatDate();
  const groups = report.groups.filter((g) => g);
  if (!groups.length) return null;
  const cell = (val: number | null, key: string) => {
    const cls = key === 'surplus' ? (val != null && val < 0 ? 'mes-rep-neg' : 'mes-rep-pos') : '';
    return <td key={key} className={cls}>{fmtNum(val)}</td>;
  };
  const rowFor = (label: string, g: Record<string, number | null>, cls = '') => (
    <tr key={label} className={cls}>
      <td className="an-td-sap pin-col">{label}</td>
      {REPORT_COLS.map(([, k]) => cell(g[k] as number | null, k))}
    </tr>
  );
  return (
    <>
      <div className="mes-report">
        <div className="mes-report-hd">
          Production report{report.targetDate ? <span className="mes-report-n"> · target {fmt(report.targetDate)}</span> : null}
        </div>
        <div className="mes-report-scroll">
          <table className="mes-report-table">
            <thead><tr><th className="pin-col">Team</th>{REPORT_COLS.map(([l]) => <th key={l}>{l}</th>)}</tr></thead>
            <tbody>
              {groups.map((g) => rowFor(teamLabel(g.team), g as unknown as Record<string, number | null>))}
              {groups.length > 1 && rowFor('All', report.all as unknown as Record<string, number | null>, 'mes-rep-total')}
            </tbody>
          </table>
        </div>
      </div>
      {report.models.length > 0 && (
        <div className="mes-report">
          <div className="mes-report-hd">By model<span className="mes-report-n"> · per-model completion</span></div>
          <div className="mes-report-scroll">
            <table className="mes-report-table">
              <thead><tr><th className="pin-col">Model</th><th>Vessels</th><th>% complete</th><th>Hrs completed</th><th>Hrs needed</th></tr></thead>
              <tbody>
                {report.models.map((m) => (
                  <tr key={m.model}>
                    <td className="an-td-sap pin-col">{m.model}</td>
                    <td>{m.vessels}</td>
                    <td className={m.pctDone != null && m.pctDone >= 100 ? 'mes-rep-pos' : ''}>{m.pctDone != null ? m.pctDone.toFixed(2) + '%' : '-'}</td>
                    <td>{fmtNum(m.hoursCompleted)}</td>
                    <td>{fmtNum(m.hoursNeeded)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// green when the day's rate of change meets-or-beats the ideal daily rate, red when behind (incl. out
// of time with work left, where the ideal collapses to 0). mirrors the Assembly Reporting page.
const dailyPaceClass = (rate: number | null | undefined, m: ReportLogMetrics): string => {
  const ideal = m.dailyIdeal ?? null;
  if (rate == null || ideal == null) return '';
  if ((m.daysAvail ?? 0) <= 0 && (m.hoursNeeded ?? 0) > 0) return 'mes-rep-neg';
  return rate >= ideal ? 'mes-rep-pos' : 'mes-rep-neg';
};

const LOG_COLS: Array<[string, keyof ReportLogMetrics]> = [
  ['HoursCompleted', 'hoursCompleted'], ['HRS Needed', 'hoursNeeded'], ['Days Avail.', 'daysAvail'],
  ['Theoretical HRS Available', 'theoretical'], ['Actual HRS 60%', 'actual60'], ['HRS Surplus', 'surplus'],
  ['Daily Rate of Change', 'dailyRate'], ['Daily Ideal Rate of Change', 'dailyIdeal'],
  ['Daily Hrs Completed', 'dailyDone'], ['Ideal Daily Hrs Completed', 'idealDone'],
];
function DailyLog({ log }: { log: ReportLogEntry[] }) {
  const [team, setTeam] = useState<'ALL' | 'GGO' | 'ALPHAS' | 'XPD'>('ALL');
  const fmt = useFormatDate();
  if (!log.length) return null;
  const rows = log.slice().reverse().map((e) => {
    const g: ReportLogMetrics = team === 'ALL' ? e.all : (e.groups.find((x) => x.team === team) as ReportLogMetrics) ?? e.all;
    return (
      <tr key={e.date}>
        <td className="an-td-sap pin-col">{fmt(e.date)}</td>
        {LOG_COLS.map(([, k]) => {
          const v = g[k];
          let cls = '';
          if (k === 'surplus') cls = v == null ? '' : v < 0 ? 'mes-rep-neg' : 'mes-rep-pos';
          else if (k === 'dailyRate') cls = dailyPaceClass(v, g);
          return <td key={k} className={cls}>{fmtNum(v)}</td>;
        })}
      </tr>
    );
  });
  return (
    <div className="mes-report mes-log">
      <div className="mes-report-hd">
        <span>Daily log<span className="mes-report-n"> · {log.length} day{log.length === 1 ? '' : 's'} (rolling)</span></span>
        <span className="mes-log-tabs">
          {(['ALL', 'GGO', 'ALPHAS', 'XPD'] as const).map((x) => (
            <button key={x} className={`mes-log-tab ${x === team ? 'on' : ''}`} aria-pressed={x === team} onClick={() => setTeam(x)} title={x}>{x === 'ALL' ? 'All' : teamLabel(x)}</button>
          ))}
        </span>
      </div>
      <div className="mes-report-scroll">
        <table className="mes-report-table">
          <thead><tr><th className="pin-col">Date</th>{LOG_COLS.map(([l]) => <th key={l}>{l}</th>)}</tr></thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  );
}

// ── board item types ────────────────────────────────────────────────────────────
interface BoardItem {
  v: OverviewVessel;
  stage: ReturnType<typeof mesStageOf>;
  days: number;
  holds: string[];
  overdue: boolean;
  stuck: boolean;
  /** the boat's seven-step build journey (journeyOf), shared by its row strip and its stage-load tick */
  steps: JourneyStep[];
  /** the journey step the boat is at (a MES stage, or 'delivery' once PDI is signed off) and its state */
  at: string;
  jstate: JourneyState;
}

// A vessel sitting idle between GRP and Assembly: GRP has marked it "sent to assembly" (grpToAssembly on
// the GRP 2.0 sheet) but it has no assembly "start date" on the production sheet yet. `days` is how long
// it has waited since GRP sent it over.
interface IdleItem {
  v: OverviewVessel;
  days: number;
  sent: Date;
  holds: string[];
  steps: JourneyStep[];
}

// one boat in the smart schedule: its priority, next operation and journey
interface ScoredRow { v: OverviewVessel; p: MesPriority; next: MesNextOp | null; steps: JourneyStep[] }

// one column of the stage load (a journey step)
interface StageCol {
  key: string;
  label: string;
  short: string;
  target: number | null;
  items: BoardItem[];
  idle: IdleItem[];
  /** Delivery only: boats already delivered (or gone), noted beside the stage, never counted in it */
  delivered: number;
  n: number;
  avg: number;
  max: number;
  over: number;
  held: number;
  late: number;
}

// hover tooltip for a boat row/chip — SAP, model and name, so the full identity is one hover away even
// when the name is clipped.
const boatTip = (v: { sap: string; model?: string; name?: string }) =>
  `${v.sap} · ${v.model || '-'}${v.name ? ` · ${v.name}` : ''}`;

// the next operation in words, with its icon: blocked on a hold, the next step, or ready to deliver
const nextOpNode = (next: MesNextOp | null): ReactNode =>
  next
    ? next.blocked
      ? <><IconPause size={13} /> {next.label} on hold</>
      : <><IconPlay size={13} /> {next.label}</>
    : <><IconCheck size={13} /> Ready to deliver</>;
const nextOpText = (next: MesNextOp | null): string =>
  next ? (next.blocked ? `${next.label} on hold` : next.label) : 'ready to deliver';

// ── a boat row: who, the journey strip, what's next, a status, and Open ─────────────────────────
// kind: 'ss' a ranked schedule row · 'al' a list row (overdue, on hold, blocked) · 'idl' an idle row.
// The whole row opens the traveler for the pointer; the Open button is the keyboard / screen-reader path.
function BoatRow({ v, steps, kind, rank, next, end, onOpen }: {
  v: OverviewVessel;
  steps: JourneyStep[];
  kind: 'ss' | 'al' | 'idl';
  rank?: number;
  next?: ReactNode;
  end?: ReactNode;
  onOpen: (v: OverviewVessel) => void;
}) {
  return (
    <li className={`mes-row mes-row--${kind}`} title={boatTip(v)} onClick={() => onOpen(v)}>
      {rank != null && <span className="mes-rk">{rank}</span>}
      <span className="mes-who">
        <span className="mes-who-n">{v.name || '-'}</span>
        <small>{v.sap} · {v.model || '-'}</small>
      </span>
      <JourneyStrip steps={steps} className="mes-bj" />
      {next != null && <span className="mes-nx">{next}</span>}
      {end != null && <span className="mes-end">{end}</span>}
      <button type="button" className="btn btn-sm mes-open" aria-label={`Open ${v.sap}`} onClick={(e) => { e.stopPropagation(); onOpen(v); }}>Open</button>
    </li>
  );
}

// a list that shows its first `cap` items and folds the rest behind "Show all N"
function MoreList<T>({ items, cap, render, empty, className = 'mes-rows' }: {
  items: T[];
  cap: number;
  render: (item: T, i: number) => ReactNode;
  empty: string;
  className?: string;
}) {
  const [all, setAll] = useState(false);
  if (!items.length) return <p className="mes-empty"><IconCheck size={14} /> {empty}</p>;
  const shown = all ? items : items.slice(0, cap);
  return (
    <>
      <ol className={className}>{shown.map(render)}</ol>
      {items.length > cap && (
        <button type="button" className="mes-more" aria-expanded={all} onClick={() => setAll((a) => !a)}>
          {all ? 'Show fewer' : `Show all ${items.length}`}
        </button>
      )}
    </>
  );
}

// the stage load's key: the journey strip's own shapes, in the words the board uses
const StageKey = () => (
  <ul className="journey-key mes-key" aria-label="Stage load key">
    <li><i className="journey-seg" data-state="current" aria-hidden="true" />Current</li>
    <li><i className="journey-seg" data-state="hold" aria-hidden="true" />Hold</li>
    <li><i className="journey-seg" data-state="late" aria-hidden="true" />Late</li>
    <li><i className="journey-seg" data-state="todo" aria-hidden="true" />Waiting</li>
  </ul>
);

// a stage's tick strip: one tick per boat in the stage (its journey state), then one waiting tick per
// idle boat (Assembly), or a single empty track when the stage has no boats
function StageTicks({ c }: { c: StageCol }) {
  const parts: string[] = [`${c.label}: ${c.n} boat${c.n === 1 ? '' : 's'}`];
  if (c.held) parts.push(`${c.held} on hold`);
  if (c.over) parts.push(`${c.over} over target`);
  if (c.late) parts.push(`${c.late} overdue`);
  if (c.idle.length) parts.push(`plus ${c.idle.length} idle, waiting to start`);
  return (
    <span className="mes-lb" role="img" aria-label={parts.join(', ')}>
      {c.items.map((i) => (
        <i key={i.v.rowId} className="journey-seg" data-state={i.jstate} title={`${i.v.sap} · ${journeyStateWord({ key: c.key, label: c.label, state: i.jstate }, c.key === 'delivery' ? LAST_STEP : 0)}`} />
      ))}
      {c.idle.map((i) => (
        <i key={i.v.rowId} className="journey-seg" data-state="todo" title={`${i.v.sap} · idle ${i.days}d`} />
      ))}
      {!c.items.length && !c.idle.length && <i className="journey-seg" data-state="todo" />}
    </span>
  );
}

// avg dwell against the stage target. The track runs to 1.5× the target, so the target mark sits at two
// thirds and an average over it reads red.
function StageMeter({ c }: { c: StageCol }) {
  if (!c.target || !c.n) return null;
  const scale = c.target * 1.5;
  const w = Math.min(100, (c.avg / scale) * 100);
  const over = c.avg > c.target;
  return (
    <span className={`mes-tm${over ? ' over' : ''}`} title={`avg ${c.avg}d, longest ${c.max}d, target ${c.target}d`}>
      <i style={{ width: `${w.toFixed(1)}%` }} />
      <b style={{ left: `${(100 / 1.5).toFixed(1)}%` }} />
    </span>
  );
}

// the boats beside a stage, not in it, under its ticks: "+N idle" at Assembly, "+N delivered" at
// Delivery. The same words and tones as the Dashboard's "Where the fleet is" (one placement,
// features/fleetPlacement).
function StageExtra({ c }: { c: StageCol }) {
  return (
    <>
      {c.idle.length > 0 && <span className="mes-ls-x is-idle" title="Sent to assembly by GRP, assembly not started yet">+{c.idle.length} idle</span>}
      {c.delivered > 0 && <span className="mes-ls-x is-dlv" title="Confirmed delivered or gone from the yard">+{c.delivered} delivered</span>}
    </>
  );
}

// the stage's figures in words
function StageNotes({ c }: { c: StageCol }) {
  return (
    <span className="mes-ls-txt">
      {c.n === 0 && !c.idle.length && <span>{c.key === 'delivery' ? 'none ready' : 'no boats'}</span>}
      {c.n > 0 && c.target != null && <><span>avg {c.avg}d · max {c.max}d</span><span>target {c.target}d</span></>}
      {c.n > 0 && c.key === 'delivery' && <span>ready for delivery</span>}
      {c.over > 0 && <span className="late">{c.over} over target</span>}
      {c.late > 0 && <span className="late">{c.late} overdue</span>}
      {c.held > 0 && <span className="hold">{c.held} on hold</span>}
    </span>
  );
}

// the SAP chips: every boat in the stage (opens its traveler), then the idle boats waiting at it
function StageChips({ c, onOpen }: { c: StageCol; onOpen: (v: OverviewVessel) => void }) {
  const [all, setAll] = useState(false);
  const fmt = useFormatDate();
  const chips: Array<{ v: OverviewVessel; tone: string; tip: string }> = [
    ...c.items.map((i) => ({
      v: i.v,
      tone: i.jstate === 'hold' ? 'h' : i.jstate === 'late' ? 'l' : c.key === 'delivery' ? 'r' : '',
      tip: `${boatTip(i.v)} · ${i.days}d in ${i.stage?.label ?? c.label}`,
    })),
    ...c.idle.map((i) => ({ v: i.v, tone: 'w', tip: `${boatTip(i.v)} · idle ${i.days}d · sent ${fmt(i.sent)}` })),
  ];
  if (!chips.length) return null;
  const shown = all ? chips : chips.slice(0, CHIP_CAP);
  return (
    <ul className="mes-saps">
      {shown.map((ch) => (
        <li key={ch.v.rowId}>
          <button type="button" className={`mes-sap${ch.tone ? ` ${ch.tone}` : ''}`} title={ch.tip} onClick={() => onOpen(ch.v)}>
            {ch.tone === 'w' && <IconHourglass size={11} />}{ch.v.sap}
          </button>
        </li>
      ))}
      {chips.length > CHIP_CAP && (
        <li><button type="button" className="mes-sap more" aria-expanded={all} onClick={() => setAll((a) => !a)}>{all ? 'fewer' : `+${chips.length - CHIP_CAP}`}</button></li>
      )}
    </ul>
  );
}

export function Board() {
  // declared before the data hooks so the report-log query can be deferred until its panel is opened.
  const [showReport, setShowReport] = useState(false);
  const fmt = useFormatDate();
  const df = useDateFormatter();
  const season = useActiveSeason();
  const { can } = useAuth();
  // read only what the viewer may read: the server refuses the rest (a 403 per load, and a denied entry
  // in the audit log), so a role like Engineering (no production:read) never asks for the production files
  const canGrp = can('grp:read');
  const canProd = can('production:read');
  const canReport = can('report:read');
  const ov = useOverview();
  const prod = useProductionVessels(undefined, canProd);
  const report = useReport(undefined, canReport);
  // the daily log only feeds the collapsed report panel, so only fetch it when that panel is open —
  // this avoids the server rebuilding the whole report a second time + a KV write on every Board load.
  const reportLog = useReportLog(undefined, showReport && canReport);
  const grp = useGrp(canGrp); // GRP 2.0 sheet - its "sent to assembly" column drives the idle-time list

  // Planning needs both the production files' hours and the report's team sizes
  const views = VIEWS.filter(([k]) => k !== 'planning' || (canProd && canReport));
  // the view, model and search stay as you left them when you come back to the page (this tab only)
  const [storedView, setView] = useTabState<View>('board.view', 'board');
  const view: View = views.some(([k]) => k === storedView) ? storedView : 'board';
  const [model, setModel] = useTabState('board.model', '');
  const [q, setQ] = useTabState('board.q', '');
  const [whatIf, setWhatIf] = useState<Record<Team, number>>({ GGO: 0, ALPHAS: 0, XPD: 0 });
  const [traveler, setTraveler] = useState<number | null>(null); // the open boat's overview row
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<unknown>(null);
  // phones fold the legend (reference text) behind its summary and get the compact stage load
  const isPhone = useMediaQuery(MQ.phone);
  const doExport = async () => {
    setExporting(true); setExportErr(null);
    try { await exportMesBoard(placement, today, undefined, df.prefs); } catch (e) { setExportErr(e); } finally { setExporting(false); }
  };

  // "today" is taken afresh with each data load, so a board left open overnight counts days right after a Sync
  const today = useMemo(startOfTodayUtc, [ov.dataUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const vessels = ov.data?.vessels ?? [];
  const rep = report.data;

  const models = useMemo(() => [...new Set(vessels.map((v) => v.model).filter(Boolean))].sort(), [vessels]);
  // a model filter that no boat of this season has (a season switch, or one kept from an earlier visit)
  // would hide every boat while the select reads "All models": drop it
  useEffect(() => { if (model && ov.data && !models.includes(model)) setModel(''); }, [model, models, ov.data]); // eslint-disable-line react-hooks/exhaustive-deps
  // the production files' boats. The map is for lookups by SAP, so a boat without one stays out of it
  // (several blanks would collapse into one entry); counts read the full list.
  const prodList = useMemo(() => prod.data?.vessels ?? [], [prod.data]);
  const prodMap = useMemo(() => {
    const m = new Map<string, VesselSummary>();
    for (const p of prodList) { const k = String(p.sap ?? '').trim(); if (k) m.set(k, p); }
    return m;
  }, [prodList]);
  const query = fold(q.trim());
  const matchMQ = (sap: string, name: string, mdl: string) =>
    (!model || mdl === model) && hasText(query, `${sap} ${name || ''} ${mdl || ''}`);
  const matches = (v: OverviewVessel) => matchMQ(v.sap, v.name, v.model);
  const matchP = (p: VesselSummary) => matchMQ(p.sap, p.name, p.model);

  // Where every boat is: the one placement the Dashboard's "Where the fleet is" uses too
  // (features/fleetPlacement). The idle signal is GRP's "sent to assembly" date (GRP 2.0 sheet) less the
  // boats whose production sheet has an assembly Start Date; while either read is in flight no boat is
  // placed as idle, so nothing moves once drawn. Without GRP access the idle list simply stays empty.
  const signal = useMemo(
    () => (canGrp && grp.data && idleReady(grp, canProd ? prod : null)
      ? idleSignalFrom(grp.data.vessels, canProd ? prod.data?.vessels : undefined)
      : null),
    [canGrp, canProd, grp.data, prod.data, grp.isError, prod.isError], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // while GRP (and, when needed, the production files) are still loading, boats GRP has sent would be
  // drawn at GRP and then jump to idle; the stage load and the idle figures wait for them instead
  const placeReady = !canGrp || idleReady(grp, canProd ? prod : null);
  const placement = useMemo(
    () => placeFleet(vessels.filter(matches), signal, today),
    [vessels, model, query, signal, today], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // on the floor (not left, not delivered), honouring the model/search filter
  const active = placement.onFloor;

  // Idle boats, longest idle first: they sit beside Assembly (a low tick and "+N idle"), not in GRP,
  // where they would otherwise read as "stuck in GRP" for every day since GRP sent them over.
  const idleItems = useMemo<IdleItem[]>(
    () => placement.idle.map((i) => ({ v: i.v, days: i.days, sent: i.sent, holds: mesHolds(i.v), steps: i.steps })),
    [placement],
  );

  // the stage buckets come straight from the placement (so the stage load agrees tick for tick with
  // every row's journey strip and with the Dashboard). overdue/on-hold are counted across ALL boats on
  // the floor (incl. idle and not started), while "stuck" only applies to boats still working a stage.
  const { buckets, wip, overdue, held } = useMemo(() => {
    const itemOf = (v: OverviewVessel, steps: JourneyStep[], at: string, jstate: JourneyState): BoardItem => {
      const st = mesStageOf(v, today);
      const enter = st?.enter ? parseOvDate(v[st.enter] as string) : null;
      const days = enter ? Math.max(0, Math.round((+today - +enter) / DAY)) : 0;
      const rem = mesRemaining(v).length;
      return {
        v, stage: st, days, holds: mesHolds(v), overdue: mesOverdue(v, today),
        stuck: !!st?.target && days > st.target && rem > 0,
        steps, at, jstate,
      };
    };
    const bk: Record<string, BoardItem[]> = {};
    const placed = new Map<OverviewVessel, BoardItem>();
    for (const s of placement.stages) {
      bk[s.key] = s.boats.map((b) => {
        const item = itemOf(b.v, b.steps, b.at, b.state);
        placed.set(b.v, item);
        return item;
      }).sort((a, b) => b.days - a.days);
    }
    const all = active.map((v) => placed.get(v) ?? itemOf(v, journeyOf(v, today), '', 'todo'));
    return {
      buckets: bk, wip: placement.staged,
      overdue: all.filter((i) => i.overdue),
      held: all.filter((i) => i.holds.length),
    };
  }, [placement, active, today]);

  const stageCols = useMemo<StageCol[]>(() => JOURNEY_STAGES.map((js) => {
    const ms = MES_STAGES.find((s) => s.key === js.key) ?? null;
    const b = buckets[js.key]!;
    const d = b.map((i) => i.days);
    return {
      key: js.key, label: js.label, short: SHORT[js.key] ?? js.label, target: ms?.target ?? null,
      items: b, idle: js.key === 'assembly' ? idleItems : [], n: b.length,
      delivered: js.key === 'delivery' ? placement.delivered.length : 0,
      avg: b.length ? Math.round(d.reduce((a, c) => a + c, 0) / b.length) : 0,
      max: b.length ? Math.max(...d) : 0,
      over: ms ? b.filter((i) => i.stuck).length : 0,
      held: b.filter((i) => i.holds.length).length,
      late: ms ? 0 : b.filter((i) => i.overdue).length,
    };
  }), [buckets, idleItems, placement]);

  // the smart schedule's order (highest priority first), shared by the Board's "next 5", the full
  // Smart schedule view and the phone's Next-up bar
  const scored = useMemo<ScoredRow[]>(
    () => active
      .map((v) => ({ v, p: mesPriority(v, today), next: mesNextOp(v), steps: journeyOf(v, today) }))
      .sort((a, b) => b.p.score - a.p.score),
    [active, today],
  );

  // opened by the overview row, so two rows that share a SAP (or have none) each open their own boat
  const travelerV = traveler != null ? vessels.find((v) => v.rowId === traveler) ?? null : null;
  const open = (v: OverviewVessel) => setTraveler(v.rowId);

  // a fact in the headline jumps to its list on the Board view
  const jump = (id: string) => {
    setView('board');
    window.setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const still = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
      el.focus({ preventScroll: true });
    }, 0);
  };

  // ── the headline facts ───────────────────────────────────────────────────────────
  // all of them honour the current model/search filter so they reconcile with the board below.
  // delivered = confirmed delivered or gone from the yard: the placement's "+N delivered" beside Delivery
  const delivered = placement.delivered.length;
  const inProd = prodList.length
    ? prodList.filter((p) => p.remaining >= INPROD_THRESHOLD_HOURS && matchP(p)).length
    : (model || query ? wip : (rep?.inProduction ?? wip));
  // completion is a figure, not a warning: the neutral accent chip (as the Dashboard's assembly average)
  const prog = rep?.buildPct ?? null;
  const teamsShort = !!(rep && rep.teamsN && rep.teamsOk < rep.teamsN);
  const targetSoon = !!(rep && rep.daysToTarget != null && rep.daysToTarget < 14);

  const loading = ov.isLoading;
  const syncing = ov.isFetching || prod.isFetching || report.isFetching || reportLog.isFetching || grp.isFetching;
  // refetch() runs even a switched-off query, so Sync only pulls what this viewer may read
  const sync = () => {
    void ov.refetch();
    if (canProd) void prod.refetch();
    if (canReport) { void report.refetch(); if (showReport) void reportLog.refetch(); }
    if (canGrp) void grp.refetch();
  };
  const asOf = ov.dataUpdatedAt ? df.time(ov.dataUpdatedAt) : null;
  const top = scored[0];

  return (
    <>
      <PageHeader
        title="Assembly Board"
        meta={<>Production control{season ? ` · season ${season}` : ''}{asOf ? <> · <span title="Data is pulled live from Smartsheet on load and on Sync">as of {asOf}</span></> : null}</>}
      >
        <div className="mes-acts">
          <button className={`btn mes-act${syncing ? ' is-busy' : ''}`} disabled={syncing} onClick={sync} title="Pull the latest from Smartsheet">
            {syncing ? <LogoSpin size={16} decorative /> : <IconRefresh size={16} />}<span className="mes-act-lbl">{syncing ? 'Syncing…' : 'Sync'}</span>
          </button>
          <button className="btn mes-act" disabled={exporting || !ov.data} onClick={() => void doExport()} title="Export the current board to Excel">
            <IconDownload size={16} /><span className="mes-act-lbl">{exporting ? 'Exporting…' : 'Excel'}</span>
          </button>
        </div>
      </PageHeader>

      {loading && <LoadingState label="Loading production data…" />}
      {ov.error && <ErrorState error={ov.error} onRetry={() => void ov.refetch()} />}

      {ov.data && (
        <div className="mes-wrap">
          {/* Views + tools */}
          <div className="mes-tbar">
            {/* the shared view switch (as Service's): tablist + tabs, roving tab stop, Left/Right to move */}
            <Segmented value={view} onChange={setView} ariaLabel="Board view" options={views.map(([k, l]) => ({ value: k, label: l }))} />
            <div className="mes-tools">
              <span className="mes-sel">
                <select className="mes-ctl-sel" aria-label="Filter by model" value={model} onChange={(e) => setModel(e.target.value)}>
                  <option value="">All models</option>
                  {models.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <IconChevronRight size={15} />
              </span>
              <label className="mes-fld">
                <IconSearch size={16} />
                <input className="mes-ctl-search" type="search" placeholder={isPhone ? 'Search boats' : 'Search name, SAP or model'} aria-label="Search name / SAP / model" value={q} onChange={(e) => setQ(e.target.value)} />
              </label>
            </div>
          </div>
          {exportErr ? <InlineError error={exportErr} onDismiss={() => setExportErr(null)} /> : null}

          {/* The headline: the board's facts as one sentence (the shared FactSentence), each figure in
              its status colour; the counts jump to their list */}
          <FactSentence>
            {prog != null
              ? <>Build completion is <Fact tone="acc" title="Fleet build completion from the manpower & hours report">{prog}%</Fact><FactEnd>.</FactEnd>{' '}</>
              : canReport && report.isSuccess ? <>Build completion isn&rsquo;t reported yet.{' '}</> : null}
            <Fact tone={overdue.length ? 'err' : undefined} icon={<IconAlertTriangle size={16} />} title="Active boats past their planned delivery date" onClick={() => jump('mes-overdue')}>
              {overdue.length} overdue
            </Fact><FactEnd>,</FactEnd>{' '}
            <Fact tone={held.length ? 'warn' : undefined} icon={<IconPause size={16} />} title="Active boats with a Hold flag" onClick={() => jump('mes-hold')}>
              {held.length} on hold
            </Fact>{' '}and{' '}
            <Fact icon={<IconHourglass size={16} />} title="Boats GRP has sent to assembly that still have no assembly start date" onClick={() => jump('mes-idle')}>
              {placeReady ? idleItems.length : '…'} idle
            </Fact>{' '}between GRP and assembly<FactEnd>.</FactEnd>
          </FactSentence>
          <FactSentence sub className="mes-facts-sub">
            <span title="On the floor: not left, not delivered">{active.length} active</span>
            {' · '}<span title="Boats working a production stage or waiting on delivery">{placeReady ? wip : '…'} in build</span>
            {' · '}<span title="Boats with labour-hours still to do (≥2h remaining) in the production files">{inProd} in production</span>
            {rep ? <>{' with '}<b title="Total labour-hours remaining across the fleet">{fmtHrs(rep.hoursLeft)}</b> hours left</> : null}
            {' · '}<span title="Confirmed delivered or gone from the yard (the Overview's Delivered or Left)"><b className="ok">{delivered}</b> delivered</span>
            {rep && rep.teamsN ? <>{' · '}<span title="Teams whose 60% capacity covers the hours needed by the target date"><b className={teamsShort ? 'warn' : 'ok'}>{rep.teamsOk} of {rep.teamsN}</b> teams on track</span></> : null}
            {rep && rep.daysToTarget != null ? <>{' · '}<span title="Calendar days until the season target date"><b className={targetSoon ? 'warn' : ''}>{rep.daysToTarget}</b> {rep.daysToTarget === 1 ? 'day' : 'days'} to target</span></> : null}
          </FactSentence>

          {/* Capacity: each team's hours left against the season target */}
          {view !== 'planning' && rep && rep.groups.length > 0 && (
            <div className="mes-cap-strip" title="Team capacity from the manpower & hours report">
              <span className="mes-cap-lbl">Capacity{rep.targetDate ? ` to ${fmt(rep.targetDate)}` : ''}</span>
              {rep.groups.map((g) => (
                <span key={g.team} className={`mes-cap-cell ${g.surplus >= 0 ? 'ok' : 'risk'}`}>
                  <b>{teamLabel(g.team)}</b>{g.pctDone != null ? ' · ' + g.pctDone + '%' : ''} · {fmtHrs(g.hoursNeeded)}h left <em>{g.surplus >= 0 ? 'on track' : 'at risk'}</em>
                </span>
              ))}
            </div>
          )}

          <div id="mes-view" role="tabpanel" aria-label={views.find(([k]) => k === view)?.[1]}>
            {view === 'planning' ? (
              <PlanningView vessels={vessels} prodList={prodList} prodState={prod.isError ? 'error' : prod.data ? 'ready' : 'loading'} onRetry={() => void prod.refetch()} rep={rep} whatIf={whatIf} setWhatIf={setWhatIf} matches={matches} today={today} onOpen={open} />
            ) : view === 'schedule' ? (
              <ScheduleView scored={scored} onOpen={open} />
            ) : (
              <>
                {/* Stage load: the journey strip enlarged, one tick per boat per stage */}
                <section className="mes-pnl mes-loadpnl" aria-labelledby="mes-load-h">
                  <div className="mes-pnl-h">
                    <h2 id="mes-load-h">Stage load</h2>
                    <span className="mes-pnl-n">{placeReady ? `${wip} boat${wip === 1 ? '' : 's'} in a stage · one tick per boat` : 'placing the boats…'}</span>
                    <StageKey />
                  </div>
                  <div className="mes-pnl-b">
                    {!placeReady ? (
                      <p className="mes-empty" role="status">Checking which boats GRP has sent to assembly…</p>
                    ) : isPhone ? (
                      <>
                        <ol className="mes-load mes-load--ph">
                          {stageCols.map((c) => (
                            <li key={c.key} className="mes-ls">
                              <span className="mes-ls-n" title={c.label}>{c.short}</span>
                              <b className={`mes-ls-c${c.n ? '' : ' z'}`}>{c.n}</b>
                              <StageTicks c={c} />
                              <StageExtra c={c} />
                            </li>
                          ))}
                        </ol>
                        <ul className="mes-tgt">
                          {stageCols.filter((c) => c.n || c.idle.length).map((c) => (
                            <li key={c.key}>
                              <b>{c.label}</b>
                              <StageMeter c={c} />
                              <StageNotes c={c} />
                              <StageChips c={c} onOpen={open} />
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <ol className="mes-load">
                        {stageCols.map((c) => (
                          <li key={c.key} className="mes-ls">
                            <div className="mes-ls-t"><span className="mes-ls-n">{c.label}</span><b className={`mes-ls-c${c.n ? '' : ' z'}`}>{c.n}</b></div>
                            <StageTicks c={c} />
                            <StageExtra c={c} />
                            <div className="mes-ls-d">
                              <StageMeter c={c} />
                              <StageNotes c={c} />
                              <StageChips c={c} onOpen={open} />
                            </div>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </section>

                <div className="mes-grid mes-grid--wide">
                  <section className="mes-pnl" aria-labelledby="mes-next-h">
                    <div className="mes-pnl-h">
                      <h2 id="mes-next-h">Smart schedule</h2>
                      <span className="mes-pnl-n">next 5, in priority order</span>
                      <button type="button" className="mes-lk" onClick={() => setView('schedule')}>Full schedule<IconChevronRight size={14} /></button>
                    </div>
                    <MoreList
                      items={scored.slice(0, 5)} cap={5} empty="No active vessels."
                      render={(row, i) => (
                        <BoatRow
                          key={row.v.rowId} kind="ss" rank={i + 1} v={row.v} steps={row.steps} onOpen={open}
                          next={<><b>{nextOpNode(row.next)}</b>{row.p.reasons.length ? <small>{row.p.reasons.join(' · ')}</small> : null}</>}
                        />
                      )}
                    />
                  </section>
                  <section className="mes-pnl" id="mes-idle" tabIndex={-1} aria-labelledby="mes-idle-h">
                    <div className="mes-pnl-h">
                      <h2 id="mes-idle-h">Idle</h2>
                      <span className="mes-pnl-n">sent from GRP, assembly not started</span>
                    </div>
                    {!placeReady ? <p className="mes-empty" role="status">Checking GRP…</p> : <MoreList
                      items={idleItems} cap={idleItems.length || 1} empty="No boats waiting between GRP and assembly."
                      render={(it) => (
                        <BoatRow
                          key={it.v.rowId} kind="idl" v={it.v} steps={it.steps} onOpen={open}
                          next={<>sent {fmt(it.sent)}{it.holds.length ? <span className="mes-tag warn"><IconPause size={11} />{it.holds.join(', ')}</span> : null}</>}
                          end={<span title="days idle since GRP sent it to assembly">{it.days} day{it.days === 1 ? '' : 's'}</span>}
                        />
                      )}
                    />}
                  </section>
                </div>

                <div className="mes-grid">
                  <section className="mes-pnl" id="mes-overdue" tabIndex={-1} aria-labelledby="mes-ov-h">
                    <div className="mes-pnl-h">
                      <h2 id="mes-ov-h">Overdue deliveries</h2>
                      <span className="mes-pnl-n">{overdue.length} past the planned delivery date</span>
                    </div>
                    <MoreList
                      items={overdue} cap={6} empty="No overdue deliveries."
                      render={(i) => {
                        const del = parseOvDate(i.v.deliveryDate);
                        const late = del ? Math.max(0, Math.round((+today - +del) / DAY)) : null;
                        return (
                          <BoatRow
                            key={i.v.rowId} kind="al" v={i.v} steps={i.steps} onOpen={open}
                            end={<><b className="late">{late != null ? `${late}d late` : 'overdue'}</b>{del ? <small>due {fmt(del)}</small> : null}</>}
                          />
                        );
                      }}
                    />
                  </section>
                  <section className="mes-pnl" id="mes-hold" tabIndex={-1} aria-labelledby="mes-hold-h">
                    <div className="mes-pnl-h">
                      <h2 id="mes-hold-h">On hold</h2>
                      <span className="mes-pnl-n">{held.length} with a Hold flag</span>
                    </div>
                    <MoreList
                      items={held} cap={6} empty="No boats on hold."
                      render={(i) => (
                        <BoatRow
                          key={i.v.rowId} kind="al" v={i.v} steps={i.steps} onOpen={open}
                          end={<><span className="mes-tag warn"><IconPause size={11} />{i.holds.join(', ')}</span>{i.overdue ? <small className="late">overdue</small> : i.stuck ? <small>{i.days}d in {i.stage!.label}</small> : null}</>}
                        />
                      )}
                    />
                  </section>
                </div>
              </>
            )}
          </div>

          {/* Production report tables + daily log - collapsed by default to keep the board clean */}
          {(rep || reportLog.data) && (
            <div className="mes-report-wrap">
              <button className="btn btn-sm mes-report-toggle" aria-expanded={showReport} onClick={() => setShowReport((s) => !s)}>
                <span className="mes-report-chev" data-open={showReport || undefined}><IconChevronRight size={14} /></span>
                {showReport ? 'Hide' : 'Show'} production report &amp; daily log
              </button>
              {showReport && (
                <>
                  {rep && <ReportTables report={rep} />}
                  {reportLog.data && <DailyLog log={reportLog.data.log} />}
                </>
              )}
            </div>
          )}

          {/* Legend - explains the three views + the terms used on the board. Reference text, so on phones
              it folds behind its summary (the summary is hidden on wider screens, where it stays open). */}
          <details className="mes-legend" open={!isPhone}>
            <summary>What the views and terms mean</summary>
            <b>Board</b> = the per-stage load and the boats idle between GRP and assembly · <b>Smart schedule</b> = what to work on next, in priority order · <b>Planning</b> = a forward capacity plan to the season target.
            <span className="mes-legend-sep">·</span>
            <b>Active</b> = on the floor (not left, not delivered) · <b>In build</b> = in a production stage or waiting on delivery · <b>Idle</b> = GRP sent it to assembly but the production sheet has no start date yet · <b>In production</b> = boats with labour-hours left in the production files · <b>Delivery</b> = PDI signed off, ready for delivery · <b>Delivered</b> = confirmed delivered or gone from the yard, noted beside Delivery (the Dashboard places every boat the same way).
          </details>
        </div>
      )}

      {travelerV && <MesTraveler v={travelerV} today={today} onClose={() => setTraveler(null)} />}

      {/* phones: the schedule's first boat, one tap from its traveler */}
      <ActionBar
        show={!!ov.data && !!top && view !== 'planning'}
        className="mes-nextbar"
        message={top ? <span className="mes-nextup"><small>Next up</small><b>{top.v.sap} · {top.v.model || '-'} · {nextOpText(top.next)}</b></span> : null}
      >
        <button type="button" className="btn btn-primary" onClick={() => top && open(top.v)}>Open</button>
      </ActionBar>
    </>
  );
}

// ── SMART SCHEDULE view ──────────────────────────────────────────────────────────
function ScheduleView({ scored, onOpen }: { scored: ScoredRow[]; onOpen: (v: OverviewVessel) => void }) {
  const stations: Record<string, { label: string; items: ScoredRow[] }> = {};
  MES_WORK.forEach(([k, l]) => (stations[k as string] = { label: l, items: [] }));
  const blocked: ScoredRow[] = [];
  scored.forEach((row) => {
    if (!row.next) return;
    if (row.next.blocked) { blocked.push(row); return; }
    stations[row.next.key as string]!.items.push(row);
  });
  const busy = MES_WORK.filter(([k]) => stations[k as string]!.items.length);

  return (
    <>
      <section className="mes-pnl" aria-labelledby="mes-order-h">
        <div className="mes-pnl-h">
          <h2 id="mes-order-h">Recommended order</h2>
          <span className="mes-pnl-n">do these first · weighs the delivery deadline, overdue, stuck, holds and near-done</span>
        </div>
        <MoreList
          items={scored} cap={16} empty="No active vessels."
          render={(row, i) => (
            <BoatRow
              key={row.v.rowId} kind="ss" rank={i + 1} v={row.v} steps={row.steps} onOpen={onOpen}
              next={<><b className={row.next?.blocked ? 'hold' : ''}>{nextOpNode(row.next)}</b>{row.p.reasons.length ? <small>{row.p.reasons.join(' · ')}</small> : null}</>}
              end={<span className="mes-score" title="Priority score: higher = do sooner">{row.p.score}</span>}
            />
          )}
        />
      </section>

      <section className="mes-pnl" aria-labelledby="mes-st-h">
        <div className="mes-pnl-h">
          <h2 id="mes-st-h"><IconTool size={15} /> Work assignment by station</h2>
          <span className="mes-pnl-n">each vessel's next actionable step, in priority order</span>
        </div>
        <div className="mes-pnl-b">
          {busy.length ? (
            <div className="mes-station-grid">
              {busy.map(([k, l]) => {
                const items = stations[k as string]!.items;
                return (
                  <div key={k as string} className="mes-station">
                    <div className="mes-station-hd"><h3>{l}</h3><span className="mes-station-n">{items.length}</span></div>
                    <MoreList
                      items={items} cap={12} empty="" className="mes-station-list"
                      render={(row) => (
                        <li key={row.v.rowId} className="mes-station-item" title={boatTip(row.v)} {...clickable(() => onOpen(row.v))}>
                          <span className="mes-si-sap">{row.v.sap}</span>
                          <span className="mes-si-name">{row.v.name || '-'}<small> · {row.v.model || '-'}</small></span>
                          <JourneyStrip steps={row.steps} size="sm" />
                          {row.p.overdue
                            ? <span className="mes-si-due late" title="Overdue"><IconAlertTriangle size={12} /><span className="sr-only">Overdue</span></span>
                            : row.p.dueDays != null && row.p.dueDays <= 14 ? <span className="mes-si-due" title="days to delivery">{row.p.dueDays}d</span> : null}
                        </li>
                      )}
                    />
                  </div>
                );
              })}
            </div>
          ) : <p className="mes-empty"><IconCheck size={14} /> Nothing ready.</p>}
        </div>
      </section>

      {blocked.length > 0 && (
        <section className="mes-pnl" aria-labelledby="mes-blk-h">
          <div className="mes-pnl-h">
            <h2 id="mes-blk-h"><IconPause size={15} /> Blocked</h2>
            <span className="mes-pnl-n">resolve holds to unblock · {blocked.length}</span>
          </div>
          <MoreList
            items={blocked} cap={14} empty="" className="mes-rows mes-rows--2"
            render={(row) => (
              <BoatRow
                key={row.v.rowId} kind="al" v={row.v} steps={row.steps} onOpen={onOpen}
                end={<span className="mes-tag warn"><IconPause size={11} />{row.next!.label}{row.p.holds.length ? ': ' + row.p.holds.join(', ') : ''}</span>}
              />
            )}
          />
        </section>
      )}
    </>
  );
}

// ── PLANNING view (finite-capacity gantt + what-if + weekly load) ─────────────────
interface PlanBoat { v: OverviewVessel; hrs: number; hrsPerBoat: number; score: number }
function PlanningView({
  vessels, prodList, prodState, onRetry, rep, whatIf, setWhatIf, matches, today, onOpen,
}: {
  vessels: OverviewVessel[];
  prodList: VesselSummary[];
  /** the production files' read: the plan is only drawn once they are in, never as an empty "all clear" */
  prodState: 'loading' | 'error' | 'ready';
  onRetry: () => void;
  rep: ReportSummary | undefined;
  whatIf: Record<Team, number>;
  setWhatIf: (f: (p: Record<Team, number>) => Record<Team, number>) => void;
  matches: (v: OverviewVessel) => boolean;
  today: Date;
  onOpen: (v: OverviewVessel) => void;
}) {
  const fmt = useFormatDate();
  const df = useDateFormatter();
  if (!rep) return <div className="mes-plan-empty">Planning needs the manpower &amp; hours report - it isn’t available yet. Press Sync, or open Assembly Reporting once.</div>;
  if (prodState === 'loading') return <InlineLoading className="mes-plan-empty" label="Loading the production files’ hours…" />;
  if (prodState === 'error') return <div className="mes-plan-empty" role="alert">The production files couldn’t be loaded, so there is nothing to plan. <button type="button" className="btn btn-sm" onClick={onRetry}>Try again</button></div>;

  const ovBySap = new Map(vessels.map((v) => [String(v.sap), v]));
  const byTeam: Record<Team, PlanBoat[]> = { GGO: [], ALPHAS: [], XPD: [] };
  let unplaced = 0;
  for (const pv of prodList) {
    if (pv.remaining < INPROD_THRESHOLD_HOURS) continue;
    const sap = String(pv.sap ?? '').trim();
    const v = sap ? ovBySap.get(sap) : undefined;
    if (!v) { unplaced++; continue; }
    // gone or delivered: nothing left to plan, even if its production file still has unticked hours
    if (v.left || v.deliveryConfirmed) continue;
    if (!matches(v)) continue;
    byTeam[teamOf(pv.model)].push({ v, hrs: pv.remaining, hrsPerBoat: pv.weights || 300, score: mesPriority(v, today).score });
  }

  const membersOf = (t: Team) => (rep.groups.find((g) => g.team === t)?.members ?? 0) + (whatIf[t] || 0);
  // a team's hours a day at 60%; at least one person's worth while anyone is in it, none when nobody is
  const capOf = (t: Team) => (membersOf(t) > 0 ? Math.max(4.8, membersOf(t) * 8 * 0.6) : 0);

  const target = parseOvDate(rep.targetDate);
  let horizon = target && target > today ? new Date(target) : addWorkingDays(today, 30);
  let latestDel = null as Date | null; // (assigned inside the per-team loop)
  const plan: Record<Team, Array<{ boat: PlanBoat; crew: number; days: number; start: Date; ready: Date; del: Date | null }>> = { GGO: [], ALPHAS: [], XPD: [] };
  const teamTot: Record<Team, number> = { GGO: 0, ALPHAS: 0, XPD: 0 };
  const teamMaxOff: Record<Team, number> = { GGO: 0, ALPHAS: 0, XPD: 0 };
  TEAMS.forEach((t) => {
    const q = byTeam[t].slice().sort((a, b) => (b.score - a.score) || (b.hrs - a.hrs));
    // a team with nobody in it (the report, or the what-if) can't build anything: no dates, no bars
    if (membersOf(t) <= 0) return;
    const sched = mesSchedule(q, membersOf(t));
    plan[t] = sched.map((it) => {
      teamTot[t] += it.boat.hrs;
      teamMaxOff[t] = Math.max(teamMaxOff[t], it.readyOff);
      const start = addWorkingDays(today, it.startOff);
      const ready = addWorkingDays(today, it.readyOff);
      const del = parseOvDate(it.boat.v.deliveryDate);
      if (ready > horizon) horizon = ready;
      if (del && (!latestDel || del > latestDel)) latestDel = del;
      return { boat: it.boat, crew: it.crew, days: Math.max(1, it.readyOff - it.startOff), start, ready, del };
    });
  });

  // delivery targets stretch the axis by at most three months past the last ready date, so one far-off
  // (or mistyped) date can't squash every bar into the left edge; a diamond beyond it sits at the end
  if (latestDel && latestDel > horizon) horizon = new Date(Math.min(+latestDel, +horizon + 90 * DAY));
  const span = Math.max(1, (+horizon - +today) / DAY);
  const pos = (d: Date) => Math.max(0, Math.min(100, (((+d - +today) / DAY) / span) * 100));
  const targetPos = target && target > today ? pos(target) : null;

  let lateN = 0, riskN = 0;
  TEAMS.forEach((t) => plan[t].forEach((it) => {
    if (it.del && it.ready > it.del) lateN++;
    else if (it.del && (+it.del - +it.ready) / DAY <= 7) riskN++;
  }));
  const recs: string[] = [];
  TEAMS.forEach((t) => {
    if (!plan[t].length || !target || !teamMaxOff[t]) return;
    const clears = addWorkingDays(today, teamMaxOff[t]);
    if (clears > target) {
      const over = Math.round((+clears - +target) / DAY);
      const u = teamTot[t] / (capOf(t) * teamMaxOff[t]);
      const hint = u > 0.85 ? 'add people to run more boats at once' : 'big boats are crew-bound (only 2-3 can work each)';
      recs.push(`${teamLabel(t)} clears ${fmt(clears)}, ${over}d past target - ${hint}`);
    }
  });

  const anyWi = whatIf.GGO || whatIf.ALPHAS || whatIf.XPD;
  const bump = (t: Team, d: number) => setWhatIf((p) => {
    const base = rep.groups.find((g) => g.team === t)?.members ?? 1;
    // down to one person (or none, for a team that has none), up to 60 extra
    return { ...p, [t]: Math.max(Math.min(0, 1 - base), Math.min(60, (p[t] || 0) + d)) };
  });

  const lanes = TEAMS.filter((t) => rep.groups.some((g) => g.team === t)).map((t) => {
    const g = rep.groups.find((x) => x.team === t)!;
    const empty = !plan[t].length;
    const clears = teamMaxOff[t] ? addWorkingDays(today, teamMaxOff[t]) : null;
    const clearsLate = !!(clears && target && clears > target);
    const util = teamMaxOff[t] ? Math.min(100, Math.round(teamTot[t] / (capOf(t) * teamMaxOff[t]) * 100)) : 0;
    const nBoats = plan[t].length;
    return (
      <section key={t} className={`mes-pnl mes-plan-lane${empty ? ' empty' : ''}`} aria-label={`${teamLabel(t)} plan`}>
        <div className="mes-plan-lane-hd">
          <h3 className="mes-plan-team">{teamLabel(t)}</h3>
          <span className="mes-plan-cap">
            {membersOf(t)} people{whatIf[t] ? <em className="mes-plan-wi"> ({whatIf[t] > 0 ? '+' : ''}{whatIf[t]})</em> : ''} · {fmtHrs(capOf(t))}h/day
            {nBoats ? <> · {nBoats} boat{nBoats === 1 ? '' : 's'} · {util}% utilised · clears <b className={clearsLate ? 'late' : ''}>{clears ? fmt(clears) : '-'}</b></> : <> · <b>no active boats</b></>}
          </span>
        </div>
        <div className="mes-plan-rows">
          {empty && membersOf(t) <= 0 && byTeam[t].length > 0 ? (
            <div className="mes-plan-noboats">Nobody is in this team, so its {byTeam[t].length} boat{byTeam[t].length === 1 ? '' : 's'} can&rsquo;t be planned. Add people with the what-if, or set the team size in Assembly Reporting.</div>
          ) : empty ? (
            <div className="mes-plan-noboats">No boats in production for this team right now. Capacity: {g.members || '-'} people, {fmtHrs(capOf(t))}h/day.</div>
          ) : plan[t].map((it) => {
            const late = !!(it.del && it.ready > it.del);
            const risk = !late && !!it.del && (+it.del - +it.ready) / DAY <= 7;
            const cls = late ? 'late' : risk ? 'risk' : 'ok';
            const left = pos(it.start), right = pos(it.ready);
            const slip = it.del ? Math.round((+it.ready - +it.del) / DAY) : null;
            return (
              <div key={it.boat.v.rowId} className="mes-plan-row" title={boatTip(it.boat.v)} {...clickable(() => onOpen(it.boat.v))}>
                <div className="mes-plan-lbl"><span className="mes-plan-sap">{it.boat.v.sap}</span><span className="mes-plan-nm">{it.boat.v.name || it.boat.v.model || '-'}</span></div>
                <JourneyStrip steps={journeyOf(it.boat.v, today)} size="sm" className="mes-plan-bj" />
                <div className="mes-plan-track">
                  {targetPos != null && <div className="mes-plan-tgt" style={{ left: `${targetPos}%` }} />}
                  <div className={`mes-plan-bar ${cls}`} style={{ left: `${left}%`, width: `${Math.max(2, right - left)}%` }} title={`${it.boat.hrs}h · crew of ${it.crew} · ~${it.days} working days`}>
                    {/* too short a bar to hold its hours shows none (the title still has them) */}
                    {right - left > 3 ? `${it.boat.hrs}h` : ''}{right - left > 7 ? ` · ${it.crew}p` : ''}
                  </div>
                  {it.del && <div className="mes-plan-del" style={{ left: `${pos(it.del)}%` }} title={`delivery target ${fmt(it.del)}`} />}
                </div>
                <div className={`mes-plan-when ${cls}`}>{fmt(it.ready)}{slip != null ? <span> {slip > 0 ? '+' : ''}{slip}d</span> : null}<span className="mes-plan-crew"> · {it.crew}p · {it.days}d</span></div>
              </div>
            );
          })}
        </div>
      </section>
    );
  });

  // month ticks
  const months: Date[] = [];
  let mDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1));
  while (mDate < horizon && months.length < 18) { months.push(new Date(mDate)); mDate = new Date(Date.UTC(mDate.getUTCFullYear(), mDate.getUTCMonth() + 1, 1)); }

  return (
    <div className="mes-plan">
      <section className="mes-pnl mes-plan-top" aria-label="Plan summary and what-if staffing">
        <div className="mes-plan-brief">
          <span className={`mes-brief-chip ${lateN ? 'bad' : 'good'}`}>{lateN} late</span>
          <span className={`mes-brief-chip ${riskN ? 'warn' : 'good'}`}>{riskN} at risk</span>
          {recs.length ? <span className="mes-brief-rec"><IconLightbulb size={14} /> {recs.join('  ·  ')}</span> : <span className="mes-brief-ok"><IconCheck size={14} /> every team clears by {target ? fmt(target) : 'target'}</span>}
        </div>

        <div className="mes-whatif">
          <span className="mes-whatif-lbl">What-if staffing</span>
          {TEAMS.filter((t) => rep.groups.some((g) => g.team === t)).map((t) => {
            const base = rep.groups.find((g) => g.team === t)?.members ?? 0;
            const d = whatIf[t] || 0;
            return (
              <span key={t} className={`mes-whatif-team ${d ? 'chg' : ''}`}>
                <b title={t}>{teamLabel(t)}</b>
                <button className="mes-wi-btn" aria-label={`One fewer person in ${teamLabel(t)}`} onClick={() => bump(t, -1)}>−</button>
                <span className="mes-wi-n">{base + d}{d ? <em> {d > 0 ? '+' : ''}{d}</em> : ''}</span>
                <button className="mes-wi-btn" aria-label={`One more person in ${teamLabel(t)}`} onClick={() => bump(t, 1)}>+</button>
              </span>
            );
          })}
          {anyWi ? <button className="mes-wi-reset" onClick={() => setWhatIf(() => ({ GGO: 0, ALPHAS: 0, XPD: 0 }))}>Reset</button> : null}
        </div>

        <p className="mes-plan-note">
          Each boat is worked by a crew of <b>2</b> (<b>3</b> for big boats over 400h), several in parallel per team, at 60% efficiency. Boats start in priority order, except that a smaller boat starts first when a big one is still waiting for a third person, so nobody stands idle. Bar = build time; <IconDiamond size={11} /> = delivery target; the vertical line is the season target{target ? ` ${fmt(target)}` : ''}.{unplaced ? <span className="mes-plan-warn"> {unplaced} not shown (no Overview match).</span> : null}
        </p>
      </section>

      {lanes.length ? lanes : <div className="mes-plan-empty">No active vessels with a model/hours match to plan.</div>}

      {lanes.length > 0 && (
        <div className="mes-plan-axis">
          <div className="mes-plan-lbl">{fmt(today)}</div>
          <span className="mes-plan-bjx" aria-hidden="true" />
          <div className="mes-plan-track">
            {months.map((m, i) => <span key={i} className="mes-plan-ax-mo" style={{ left: `${pos(m)}%` }}>{df.month(m, { year: false })}</span>)}
            {targetPos != null && (
              // is-end: past mid-axis, phones hang the label leftwards off the target so it stays on screen
              <span className={`mes-plan-ax-tgt${targetPos > 50 ? ' is-end' : ''}`} style={{ left: `${targetPos}%` }}><IconTriangleUp size={11} /> Target {fmt(target)}</span>
            )}
          </div>
          <div className="mes-plan-when">{fmt(horizon)}</div>
        </div>
      )}

      {lanes.length > 0 && <WeeklyGrid plan={plan} rep={rep} capOf={capOf} today={today} target={target} />}
    </div>
  );
}

function WeeklyGrid({
  plan, rep, capOf, today, target,
}: {
  plan: Record<Team, Array<{ crew: number; startOff?: number; readyOff?: number; start: Date; ready: Date }>>;
  rep: ReportSummary;
  capOf: (t: Team) => number;
  today: Date;
  target: Date | null;
}) {
  const df = useDateFormatter();
  const teams = TEAMS.filter((t) => rep.groups.some((g) => g.team === t));
  if (!teams.length) return null;
  const weeksToTarget = target && target > today ? Math.ceil((+target - +today) / (7 * DAY)) : 4;
  const N = Math.min(10, Math.max(4, weeksToTarget + 2));
  const monday = new Date(today);
  monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
  const weekStarts = Array.from({ length: N }, (_, i) => { const d = new Date(monday); d.setUTCDate(d.getUTCDate() + i * 7); return d; });
  const fmtW = (d: Date) => df.date(d, { year: 'never' });
  const targetWeek = target ? Math.floor((+target - +monday) / (7 * DAY)) : -1;
  const weekIdxOf = (date: Date) => Math.floor((+date - +monday) / (7 * DAY));

  const rows = teams.map((t) => {
    const weeklyCap = 5 * capOf(t);
    const weekly = new Array(N).fill(0);
    plan[t].forEach((b) => {
      const rate = b.crew * 8 * 0.6;
      // step day-by-day across the boat's build window, bucketing crew-hours into weeks
      for (let d = new Date(b.start); d < b.ready; d.setUTCDate(d.getUTCDate() + 1)) {
        const g = d.getUTCDay();
        if (g === 0 || g === 6) continue;
        const wi = weekIdxOf(d);
        if (wi >= 0 && wi < N) weekly[wi] += rate;
      }
    });
    return (
      <div key={t} className="mes-wk-row">
        <div className="mes-wk-team" title={t}>{teamLabel(t)}<span className="mes-wk-cap">cap {Math.round(weeklyCap)}h/wk</span></div>
        {weekly.map((committed, i) => {
          const u = weeklyCap > 0 ? committed / weeklyCap : 0;
          const cls = u >= 0.99 ? 'full' : u > 0.6 ? 'high' : u > 0.05 ? 'mid' : 'idle';
          return <div key={i} className={`mes-wk-cell ${cls} ${i === targetWeek ? 'tgt' : ''}`} title={`${Math.round(committed)}h of ${Math.round(weeklyCap)}h available`}>{committed > 0 ? Math.round(committed) : ''}</div>;
        })}
      </div>
    );
  });

  return (
    <section className="mes-pnl mes-wk" aria-labelledby="mes-wk-h">
      <div className="mes-pnl-h">
        <h2 id="mes-wk-h">Weekly load</h2>
        <span className="mes-pnl-n">build hours committed per team per week · filled = at capacity</span>
      </div>
      <div className="mes-pnl-b">
        <div className="mes-wk-grid">
          <div className="mes-wk-row mes-wk-head">
            <div className="mes-wk-team" />
            {weekStarts.map((d, i) => <div key={i} className={`mes-wk-cell ${i === targetWeek ? 'tgt' : ''}`}>{fmtW(d)}</div>)}
          </div>
          {rows}
        </div>
      </div>
    </section>
  );
}
